import { Component, EventEmitter, Input, Output, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { Filter } from 'src/app/shared/models/filter';
import { FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';

@Component({
    selector: 'cdp-filter-chips-panel',
    templateUrl: './filter-chips-panel.component.html',
    standalone: false
})
export class FilterChipsPanelComponent implements OnDestroy {
    @Input() filters: ReadonlyArray<Filter>;
    private _form: FormGroup;
    private formSub?: Subscription;

    @Input()
    set form(newForm: FormGroup) {
        if (this.formSub) {
            this.formSub.unsubscribe();
        }
        this._form = newForm;
        if (this._form) {
            this.formSub = this._form.valueChanges.subscribe(() => {
                this.cdr.markForCheck();
            });
        }
    }
    get form(): FormGroup {
        return this._form;
    }

    @Output() filterChipClose: EventEmitter<Filter> = new EventEmitter<Filter>();
    @Output() resetAllFilters: EventEmitter<void> = new EventEmitter<void>();

    constructor(private cdr: ChangeDetectorRef) {}

    ngOnDestroy() {
        if (this.formSub) {
            this.formSub.unsubscribe();
        }
    }

    private isEmptyChipValue(val: any): boolean {
        return val === undefined || val === null || val === '';
    }

    get filterChips(): Array<Filter> {
        const result: Array<Filter> = [];

        if (this.filters && this.form) {
            this.filters.forEach(config => {
                const value = this.form.get(config.formControlName)?.value;
                if (config.filterType === 'min-max') {
                    const minValue = this.form.get(config.formControlName + 'Min')?.value;
                    const maxValue = this.form.get(config.formControlName + 'Max')?.value;
                    if (!this.isEmptyChipValue(minValue)) {
                        result.push({ ...config, selectionMin: minValue, selectionMax: undefined });
                    }
                    if (!this.isEmptyChipValue(maxValue)) {
                        result.push({ ...config, selectionMin: undefined, selectionMax: maxValue });
                    }
                } else if (config.filterType === 'date-picker') {
                    const minValue = this.form.get(config.formControlName + 'Min')?.value;
                    const maxValue = this.form.get(config.formControlName + 'Max')?.value;
                    if (!this.isEmptyChipValue(minValue)) {
                        result.push({ ...config, dateMin: minValue, dateMax: undefined });
                    }
                    if (!this.isEmptyChipValue(maxValue)) {
                        result.push({ ...config, dateMin: undefined, dateMax: maxValue });
                    }
                } else if (
                    (Array.isArray(value) &&
                        value.length > 0 &&
                        value.some(v => v !== undefined && v !== null && v !== '')) ||
                    (typeof value !== 'object' && value !== undefined && value !== null && value !== '')
                ) {
                    result.push({ ...config });
                }
            });
        }

        return result;
    }

    chipLabel(chipInfo: Filter): string {
        const value = this.form?.get(chipInfo.formControlName)?.value;
        if (chipInfo.filterType === 'min-max') {
            if (Number.isFinite(chipInfo.selectionMin)) {
                return `${chipInfo.label} >= ${chipInfo.selectionMin}`;
            }
            if (Number.isFinite(chipInfo.selectionMax)) {
                return `${chipInfo.label} <= ${chipInfo.selectionMax}`;
            }
        } else if (chipInfo.filterType === 'date-picker') {
            if (chipInfo.dateMin) {
                return `${chipInfo.label} on or after ${new Date(chipInfo.dateMin).toLocaleDateString()}`;
            }
            if (chipInfo.dateMax) {
                return `${chipInfo.label} on or before ${new Date(chipInfo.dateMax).toLocaleDateString()}`;
            }
        } else if (Array.isArray(value)) {
            return value.length > 1
                ? `${chipInfo.label} (${value.length})`
                : `${chipInfo.label}: ${value[0]?.display ?? value[0]}`;
        } else if (value) {
            return `${chipInfo.label}: ${value.display ?? value}`;
        }
        return '';
    }

    chipTitle(chipInfo: Filter): string {
        const value = this.form?.get(chipInfo.formControlName)?.value;
        return Array.isArray(value) && value.length > 1 && chipInfo.filterType !== 'min-max'
            ? value.map((selection: any) => selection.display ?? selection).join('\r\n')
            : '';
    }

    closeChip(chip: Filter): void {
        let response: Filter;
        if (chip.filterType === 'min-max') {
            if (chip.selectionMin) {
                response = {
                    ...chip,
                    formControlName: `${chip.formControlName}Min`
                };
            } else {
                response = {
                    ...chip,
                    formControlName: `${chip.formControlName}Max`
                };
            }
        } else if (chip.filterType === 'date-picker') {
            if (chip.dateMin) {
                response = {
                    ...chip,
                    formControlName: `${chip.formControlName}Min`
                };
            } else {
                response = {
                    ...chip,
                    formControlName: `${chip.formControlName}Max`
                };
            }
        } else {
            response = chip;
        }

        this.filterChipClose.emit(response);
    }

    trackByFn(index: number) {
        return index;
    }
}
